import React, { ChangeEvent, useCallback, useMemo, useState, useEffect } from 'react';
import { DailyAppointmentsProps, Topic, Slot, AppointmentData } from './appointment-list.types';
import '@/assets/styles/components/appointment-list.styles.scss';
import Pagination from '@/components/pagination';
import { ActionModal, DialogModal } from '@/components';
import { NavigationPath } from '@/router/navigation-path';
import { useNavigate } from 'react-router';
import { useApi } from '@/api/useApi';
import { useBoutiqueMenu, useMutationProvider, useQueryProvider } from '@/hooks';
import { EMPTY_VALUE_LABEL } from '@/screens/boutique-classroom/boutique-classroom.constants';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import LoadingSpinner from '@/components/loading-spinner';
import { useAppointmentStatus } from '@/hooks/use-appointment-status';

import NoSlotImage from '@/assets/images/no-slot.png';
import NoAppointmentsImage from '@/assets/images/no-appointments.png';
import User2 from '@/assets/images/user2.png';
import NotificationManager from '@/managers/notification-manager.tsx';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import ExceptionManager from '@/managers/exception-manager.tsx';
import { useMeeting } from '@/context/meeting-context';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { CALL_STATE_TYPES } from '@/constants/socket.ts';
import ClassNames from 'classnames';
import { useDevicePermissions } from '@/context/device-permission-context.tsx';
import { ManualCalendarSlotUpdateRequest, ManualCalendarSlotUpdateRequestLiveServiceTypeEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { BackupRoomStudentInvitationRequest, ExpertDailySlotResponse, SlotRemovalRequest, SlotRemovalRequestSlotTypeEnum } from '@dopingtech/live_expert_bff_api_ts';

import CircleLink from '@/assets/svg/link-circle-blue.svg';
import clsx from 'clsx';
import { useFeatureFlag } from 'configcat-react';
import { toast } from 'react-toastify';
import { If } from 'vite-plugin-react-control-statements';

const TIMEZONE = 'Europe/Istanbul';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault(TIMEZONE);

const modalContent = {
	confirm: {
		title: 'Randevuyu Neden İptal Etmek İstiyorsunuz?',
		options: [
			{
				label: 'Randevuyu öğrenci iptal etmek istedi',
				value: 'studentDeleteRequest'
			},
			{
				label: 'Randevuyu kişisel sebeplerle iptal etmek istiyorum',
				value: 'expertDeleteRequest'
			}
		],
		primaryButton: 'Randevuyu İptal Et',
		secondaryButton: 'Vazgeç'
	},
	reasonInput: {
		title: 'Randevuyu İptal Etme Sebebinizi Açıklayın',
		primaryButton: 'Randevuyu İptal Et',
		secondaryButton: 'Vazgeç'
	},
	success: {
		title: 'Randevun iptal edildi',
		mainText: 'Görüşmenin öğrenci talebi doğrultusunda iptal edildiği veliye iletilecektir.',
		primaryButton: 'Mesaj Gönder',
		secondaryButton: 'Kapat'
	},
	successExpertAppointmentDelete: {
		title: 'Randevun iptal edildi',
		mainText: 'Ay içinde 3’ten fazla iptal yapmanız durumunda lütfen süpervizörünüzü bilgilendirin.',
		primaryButton: 'Mesaj Gönder',
		secondaryButton: 'Kapat'
	}
};

interface CalendarSlotToDelete {
	expertCalendarSlotId: number;
	slotType: string;
}

interface SlotError {
	error: string;
	message: string;
}

const ITEMS_PER_PAGE = 6;

export const DailyAppointments: React.FC<DailyAppointmentsProps> = ({ appointments, selectedDay, isLoading, activeTab, onTabChange, counts, liveServiceType, onUnsavedChangesChange }) => {
	const navigation = useNavigate();
	const { AppointmentControllerApi, CalendarControllerApi, MeetingControllerApi, ExpertControllerApi, BackupRoomControllerApi } = useApi();
	const { onNavigateAppointmentMeetRoom, setActiveAppointment, setIsAppointmentAvailable, hasActiveMeeting, callState } = useMeeting();
	const queryClient = useQueryClient();

	const [currentPage, setCurrentPage] = useState(1);
	const [isSlotSectionOpen, setIsSlotSectionOpen] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [addedSlots, setAddedSlots] = useState<number[]>([]);
	const [removedSlots, setRemovedSlots] = useState<SlotRemovalRequest[]>([]);
	const [isActionModalOpen, setIsActionModalOpen] = useState(false);
	const [selectedDeleteReasonOption, setSelectedDeleteReasonOption] = useState(undefined);
	const [actionModalStep, setActionModalStep] = useState<'confirm' | 'success'>('confirm');
	const [cancellationReason, setCancellationReason] = useState('');
	const [selectedAppointmentId, setSelectedAppointmentId] = useState<number | null>(null);
	const [topics, setTopics] = useState<Topic[]>([]);
	const [availableSlots, setAvailableSlots] = useState<Slot[]>([]);
	const { controlPermission } = useDevicePermissions();

	const sendBackupRoomInvitationToStudentMutation = useMutationProvider((data: BackupRoomStudentInvitationRequest) =>
		BackupRoomControllerApi.sendBackupRoomInvitationToStudent({ interactionId: data?.interactionId, studentId: data?.studentId })
	);

	const { value: isActiveFromOutsideMeetLink } = useFeatureFlag('isActiveFromOutsideMeetLink', false);

	const [isSendedBackupLink, setIsSendedBackupLink] = useState(false);

	const cancelAppointmentMutation = useMutationProvider(
		({ appointmentId, cancellationReasonString }: { appointmentId: number; cancellationReasonString: string }) =>
			AppointmentControllerApi.cancelAppointment(appointmentId, cancellationReasonString),
		{
			onSuccess: () => {
				let nextModalStep: 'success' | 'successExpertAppointmentDelete' = 'success';

				if (selectedDeleteReasonOption === 'expertDeleteRequest') {
					nextModalStep = 'successExpertAppointmentDelete';
				}

				setActionModalStep(nextModalStep);
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.findAvailableExpertCalendarSlotsOfDay]
				});
			}
		}
	);

	const toggleSlotMutation = useMutationProvider(() => CalendarControllerApi.switchSlotActivationForToday(), {
		onSuccess: async response => {
			if (response.data.data) {
				await refetchSlots();
			}
		}
	});

	const getMeetingInformation = useMutationProvider((interactionId: string) => MeetingControllerApi.getMeetingRoomInformation(interactionId), {});

	const saveSlotsMutation = useMutationProvider((updateRequest: ManualCalendarSlotUpdateRequest) => CalendarControllerApi.saveManualCalendarSlots(updateRequest), {
		onSuccess: async () => {
			const date = dayjs(selectedDay.date).tz(TIMEZONE).format('YYYY-MM');
			await refetchSlots();
			NotificationManager.notifySaveSlotChanges();
			await queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.countExpertCalendarSlotAppointmentBetweenDates, date]
			});
			await queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.getExpertActiveSlotCount]
			});
			setAddedSlots([]);
			setRemovedSlots([]);
		},
		onError: (e: { response: { data: SlotError } }) => {
			if (e?.response?.data as unknown as SlotError) {
				const data = e?.response?.data as SlotError;
				if (data.error === 'LAM117' || data.message === 'New active slot count is under the 60 slot limit. Please update your slot over limit.') {
					setIsModalOpen(true);
				} else {
					ExceptionManager.errorHandler(e);
				}
			}
		}
	});

	const { data: expertDetailInfo } = useQueryProvider([QUERY_KEYS.getLoggedInExpertDetailInfo], () => ExpertControllerApi.getLoggedInExpertDetailInfo(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		enabled: true
	});

	useQueryProvider([QUERY_KEYS.findAppointmentTopics], () => AppointmentControllerApi.findAppointmentTopics(), {
		staleTime: Infinity,
		enabled: selectedDay.hasAppointment,
		refetchOnWindowFocus: true,
		select: response => response.data.data,
		onSuccess: data => {
			setTopics(data);
		}
	});

	useQueryProvider([QUERY_KEYS.isSlotActivatedForToday], () => CalendarControllerApi.isSlotActivatedForToday(), {
		onSuccess: response => {
			setIsSlotSectionOpen(response.data.data || false);
		},
		refetchOnWindowFocus: true,
		refetchOnMount: 'always',
		staleTime: Infinity
	});

	const { refetch: refetchSlots } = useQueryProvider(
		[QUERY_KEYS.findAvailableExpertDailySlots, selectedDay?.date],
		() => {
			const formattedDate = selectedDay?.date ? dayjs(selectedDay.date).tz(TIMEZONE).format('YYYY-MM-DD') : '';

			return CalendarControllerApi.findAllExpertCalendarSlotsOfDay(formattedDate, liveServiceType);
		},
		{
			enabled: !!selectedDay?.date,
			onSuccess: response => {
				const formattedSlots = response?.data?.data?.map((slotData: ExpertDailySlotResponse) => {
					const timeStr = slotData?.slot?.slotStartTime as unknown as string;
					const [hours, minutes] = timeStr.split(':');

					return {
						id: slotData.id,
						expertCalendarId: slotData.expertCalendarId,
						slotId: slotData?.slot?.id,
						time: `${hours}:${minutes}`,
						slotStatus: slotData.slotStatus,
						slotType: slotData.slotType
					};
				});
				setAvailableSlots(formattedSlots);
			}
		}
	);

	const { hasBoutiqueAccess } = useBoutiqueMenu();
	const isBoutiquePlannedLecture = hasBoutiqueAccess && liveServiceType === ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.PlannedLecture;

	const getSubjectName = (appointment: AppointmentData) => appointment?.topicName || (isBoutiquePlannedLecture ? EMPTY_VALUE_LABEL : 'Konu Bulunamadı');

	const getTopicDescription = (topicId: number) => {
		const topic = topics.find(t => t.id === topicId);

		return topic?.description || 'Konu bulunamadı';
	};

	const paginatedAppointments = useMemo(() => {
		if (!appointments?.content) return [];

		const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
		return appointments.content.slice(startIndex, startIndex + ITEMS_PER_PAGE);
	}, [appointments?.content, currentPage]);

	const showSlotGrid = useMemo(() => {
		const isToday = dayjs(selectedDay.date).tz(TIMEZONE).isSame(dayjs(), 'day');
		return (!isToday || (isToday && isSlotSectionOpen)) && availableSlots.length > 0 && activeTab !== 'cancelled';
	}, [selectedDay.date, isSlotSectionOpen, availableSlots, activeTab]);

	const totalPages = Math.ceil((appointments?.totalElements || 0) / ITEMS_PER_PAGE);

	const handleSlotToggle = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.checked;
		setIsSlotSectionOpen(newValue);

		try {
			await toggleSlotMutation.mutateAsync();
		} catch (error) {
			console.error('Error toggling slot:', error);
			setIsSlotSectionOpen(!newValue);
		}
	}, []);

	const handleCancelAppointment = async (appointmentId: number) => {
		try {
			let deleteReason = 'Öğrenci talebiyle iptal edildi';

			if (selectedDeleteReasonOption === 'expertDeleteRequest') {
				deleteReason = 'Randevuyu kişisel sebeplerle iptal ettim';
			}

			const cancellationReasonString = JSON.stringify({
				reason: deleteReason,
				cancellationNote: cancellationReason || ''
			});

			await cancelAppointmentMutation.mutateAsync({
				appointmentId,
				cancellationReasonString
			});
		} catch (error) {
			console.error('Error cancelling appointment:', error);
		}
	};

	const handleSlotClick = useCallback(
		(e: React.MouseEvent, slot: Slot) => {
			e.preventDefault();

			if (slot.slotStatus === 'EMPTY') {
				if (!addedSlots.includes(slot.slotId)) {
					setAddedSlots(prev => [...prev, slot.slotId]);
					setRemovedSlots(prev => prev.filter(item => item.expertCalendarSlotId !== slot.id));
				} else {
					setAddedSlots(prev => prev.filter(id => id !== slot.slotId));
				}
			} else if (slot.slotStatus === 'AVAILABLE') {
				const slotToRemove = {
					expertCalendarSlotId: slot.id,
					slotType: slot.slotType as SlotRemovalRequestSlotTypeEnum
				};

				const isAlreadyRemoved = removedSlots.some(item => item.expertCalendarSlotId === slot.id);

				if (!isAlreadyRemoved) {
					setRemovedSlots(prev => [...prev, slotToRemove]);
					setAddedSlots(prev => prev.filter(id => id !== slot.slotId));
				} else {
					setRemovedSlots(prev => prev.filter(item => item.expertCalendarSlotId !== slot.id));
				}
			}
		},
		[addedSlots, removedSlots]
	);

	const isSlotActive = (slot: Slot) => {
		if (slot.slotStatus === 'EMPTY') {
			return addedSlots.includes(slot.slotId);
		} else if (slot.slotStatus === 'AVAILABLE') {
			return !removedSlots.some(item => item.expertCalendarSlotId === slot.id);
		}
		return false;
	};

	const handleSaveChanges = async () => {
		try {
			const updateRequest: ManualCalendarSlotUpdateRequest = {
				slotDate: dayjs(selectedDay.date).tz(TIMEZONE).format('YYYY-MM-DD') || '',
				slotIdsToAdd: addedSlots,
				calendarSlotsToDelete: removedSlots,
				liveServiceType: liveServiceType
			};

			await saveSlotsMutation.mutateAsync(updateRequest);
		} catch (error) {
			console.error('Değişiklikler kaydedilirken hata oluştu:', error);
		}
	};

	const onNavigateRecurringCalendar = () => {
		if (liveServiceType === ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.PlannedLecture) {
			navigation(NavigationPath.gotItPlannedRecurringCalendar);
		} else if (liveServiceType === ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.OneOnOneLecture) {
			navigation(NavigationPath.gotItRecurringCalendar);
		} else {
			navigation(NavigationPath.coachRecurringCalendar);
		}
	};

	const getCancellationMessage = (appointmentUpdaterType: 'STUDENT' | 'EXPERT' | undefined): string => {
		switch (appointmentUpdaterType) {
			case 'STUDENT':
				return 'Öğrenci iptal etti';
			case 'EXPERT':
				return 'İptal ettiniz';
			default:
				return 'İptal edildi';
		}
	};

	const handleModalClose = () => {
		setIsActionModalOpen(false);
		setActionModalStep('confirm');
		setSelectedAppointmentId(null);
		setSelectedDeleteReasonOption(undefined);
		setCancellationReason('');

		const AppointmentDate = dayjs(selectedDay.date).tz(TIMEZONE).format('YYYY-MM-DD');
		const date = dayjs(selectedDay.date).format('YYYY-MM');
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.getAppointmentCancellationCount, date]
		});
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.countExpertCalendarSlotAppointmentBetweenDates, date]
		});
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.findAppointmentsOfTheDay, AppointmentDate, 'CREATED']
		});
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.findAppointmentsOfTheDay, AppointmentDate, 'CANCELLED']
		});
	};

	const handleCompleteReport = (appointment: AppointmentData) => {
		navigation(NavigationPath.coachPastMeetingsDetail, {
			state: {
				interactionId: appointment?.interactionId || appointment?.appointmentId
			}
		});
	};

	const onClickhandleNavigateMeetingRoom = (appointment: AppointmentData) => {
		controlPermission(() => handleNavigateMeetingRoom(appointment));
	};

	const onSendLinkToStudent = async (appointment: AppointmentData) => {
		const entity: BackupRoomStudentInvitationRequest = {};

		console.log('appointment', appointment);

		entity.studentId = appointment?.studentId;
		entity.interactionId = appointment?.interactionId;

		const result = await sendBackupRoomInvitationToStudentMutation.mutateAsync(entity);

		if (result) {
			setIsSendedBackupLink(true);

			setTimeout(() => {
				setIsSendedBackupLink(false);
			}, 2000);
		}

		toast.success('Görüşme linki öğrenciye gönderildi!');
	};

	const handleNavigateMeetingRoom = async (appointment: AppointmentData) => {
		const response = await getMeetingInformation.mutateAsync(appointment?.interactionId);
		const meetingData = response?.data?.data;

		if (meetingData) {
			const appointmentMeetRoomData = {
				roomId: meetingData?.roomId,
				providerParticipantId: meetingData?.providerParticipantId,
				videoCallProviderInfo: {
					host: meetingData?.videoCallProviderInfo?.host,
					name: meetingData?.videoCallProviderInfo?.name
				},
				token: meetingData?.token,
				meetingId: meetingData?.meetingId,
				endDate: meetingData?.endDate,
				meetingLastConnectionDate: meetingData?.meetingLastConnectionDate,
				extensionNotificationDate: meetingData?.extensionNotificationDate,
				studentName: `${appointment?.studentName} ${appointment?.studentSurname}`,
				studentAvatar: appointment?.studentAvatarUrl || null,
				coachName: `${expertDetailInfo?.expert?.name} ${expertDetailInfo?.expert?.surname}`,
				coachAvatar: expertDetailInfo?.expert?.profilePicture,
				coachId: expertDetailInfo?.expert?.id,
				coachSubject: getTopicDescription(appointment?.topicId),
				searchingLessonName: getTopicDescription(appointment?.topicId),
				interactionId: appointment?.interactionId,
				liveServiceGroup: meetingData?.liveServiceGroup
			};

			onNavigateAppointmentMeetRoom(appointmentMeetRoomData);
		}
	};

	const handleCloseSlotWarningModal = async () => {
		const AppointmentDate = dayjs(selectedDay.date).tz(TIMEZONE).format('YYYY-MM-DD');
		setAddedSlots([]);
		setRemovedSlots([]);
		await queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.findAppointmentsOfTheDay, AppointmentDate, 'CREATED']
		});
		await queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.findAppointmentsOfTheDay, AppointmentDate, 'CANCELLED']
		});
		setIsModalOpen(false);
	};

	const navigateToMessages = () => {
		navigation(NavigationPath.messages, {
			state: {
				studentId: paginatedAppointments?.find(x => x?.appointmentId === selectedAppointmentId)?.studentId
			}
		});
	};

	const navigateToProviderTest = () => {
		navigation(NavigationPath.meetProviderTest);
	};

	const handleModalConfirm = async () => {
		if (actionModalStep === 'confirm') {
			if (selectedDeleteReasonOption === 'expertDeleteRequest') {
				setActionModalStep('reasonInput');
			} else {
				await handleCancelAppointment(selectedAppointmentId as number);
			}
		} else if (actionModalStep === 'reasonInput') {
			await handleCancelAppointment(selectedAppointmentId as number);
		}
	};

	const renderActionModal = (appointmentId: number) => {
		if (!isActionModalOpen || appointmentId !== selectedAppointmentId) {
			return null;
		}

		return (
			<ActionModal
				isOpen={true}
				step={actionModalStep}
				isLoading={cancelAppointmentMutation.isLoading}
				contents={modalContent}
				setSelectedDeleteReasonOption={setSelectedDeleteReasonOption}
				selectedDeleteReasonOption={selectedDeleteReasonOption}
				cancellationReason={cancellationReason}
				setCancellationReason={setCancellationReason}
				onClose={handleModalClose}
				onConfirm={handleModalConfirm}
				onSendMessage={navigateToMessages}
			/>
		);
	};

	const appointmentStatusMap = useAppointmentStatus(appointments?.content || []);

	useEffect(() => {
		onUnsavedChangesChange?.(addedSlots.length > 0 || removedSlots.length > 0);
	}, [addedSlots, removedSlots]);

	useEffect(() => {
		setAddedSlots([]);
		setRemovedSlots([]);
	}, [selectedDay.date]);

	useEffect(() => {
		if (paginatedAppointments && paginatedAppointments.length > 0) {
			const upcomingAppointment = paginatedAppointments.find(appointment => {
				const statusData = appointmentStatusMap[appointment.appointmentId];
				return statusData?.status === 'UPCOMING' || statusData?.status === 'ACTIVE';
			});

			if (upcomingAppointment) {
				setActiveAppointment({
					appointmentId: upcomingAppointment.appointmentId,
					appointmentStartDate: upcomingAppointment.appointmentStartDate,
					appointmentEndDate: upcomingAppointment.appointmentEndDate,
					studentName: upcomingAppointment.studentName,
					studentSurname: upcomingAppointment.studentSurname,
					studentGradeName: upcomingAppointment.studentGradeName,
					avatarUrl: upcomingAppointment.avatarUrl,
					interactionId: upcomingAppointment.interactionId
				});
				setIsAppointmentAvailable(true);
			} else {
				setActiveAppointment(null);
				setIsAppointmentAvailable(false);
			}
		}
	}, [paginatedAppointments, appointmentStatusMap, callState, hasActiveMeeting]);

	return (
		<div className="daily-appointments">
			{isLoading ? (
				<LoadingSpinner loadingText={'Yükleniyor'} />
			) : !selectedDay?.hasSlot ? (
				<div className="empty-state">
					<img
						src={NoSlotImage}
						alt="No slots"
					/>
					<h3>Bugün için hiç slot açmadınız!</h3>
					<p>Tekrarlayan takvimde bugün için slot açmamış görünüyorsunuz. Slot açmak için takvime gidebilir bugün ve slot açmadığınız diğer günler için slotlarınızı açabilirsiniz.</p>
					<button
						className="empty-state-button"
						onClick={onNavigateRecurringCalendar}>
						Tekrarlayan Takvime Git
					</button>
				</div>
			) : (
				<>
					{(counts?.active > 0 || counts?.cancelled > 0) && (
						<div className="appointments-header">
							<div className="appointment-tabs">
								<div
									className={`tab ${activeTab === 'active' ? 'active' : ''}`}
									onClick={() => onTabChange('active')}
									data-tab="active">
									<span>Günün Randevuları</span>
									<span className="count">{counts?.active}</span>
								</div>
								{counts?.cancelled > 0 && (
									<div
										className={`tab ${activeTab === 'cancelled' ? 'active' : ''}`}
										onClick={() => onTabChange('cancelled')}
										data-tab="cancelled">
										<span>İptal Edilenler</span>
										<span className="count">{counts?.cancelled}</span>
									</div>
								)}
							</div>

							<button
								className={ClassNames('connection-test-button', {
									disabled: callState === CALL_STATE_TYPES.CONNECT
								})}
								onClick={() => controlPermission(navigateToProviderTest)}
								disabled={callState === CALL_STATE_TYPES.CONNECT}>
								<i className={'icons-wifi'}></i>
								Bağlantını Test Et
							</button>
						</div>
					)}

					{activeTab === 'active' && !counts.active ? (
						<div className="empty-state">
							<img
								src={NoAppointmentsImage}
								alt="No appointments"
							/>
							<h3>Bugüne ait henüz randevunuz bulunmamaktadır.</h3>
							<p>Bugüne ait açık slotlarınız bulunmakta fakat herhangi bir randevu bulunmamaktadır.</p>
						</div>
					) : (
						<>
							<div className="appointments-table">
								<div className="table-header">
									<div className="col-time">Saat</div>
									<div className="col-student">Öğrenci</div>
									<div className="col-subject">Görüşme Konusu</div>
									<div className="col-count">Görüşme Sayısı</div>
									<div className="col-actions">{activeTab === 'active' ? 'İşlemler' : 'İptal Durumu'}</div>
								</div>

								<div className="table-body">
									{paginatedAppointments.map(appointment => {
										const statusData = appointmentStatusMap[appointment.appointmentId] || {
											status: 'SCHEDULED',
											isInOneHour: false,
											secondsLeft: 0
										};
										return (
											<div
												key={appointment.appointmentId}
												className="appointment-row">
												<div className="col-time">
													{new Date(appointment?.appointmentStartDate).toLocaleTimeString('tr-TR', {
														hour: '2-digit',
														minute: '2-digit'
													})}
												</div>

												<div className="col-student">
													<If condition={!!appointment?.isTopRanked}>
														<div className="student-top-ranked">
															<i className="icons-medal" />
															Derece Öğrencisi
														</div>
													</If>
													<div className="student-info">
														<img
															src={appointment?.avatarUrl || User2}
															alt="studentAvatar"
															className="avatar"
														/>
														<div className="student-details">
															<div className="name">
																{appointment?.studentName} {appointment?.studentSurname}
															</div>
															<div className="grade">{appointment?.studentGradeName}</div>
														</div>
													</div>
												</div>

												<div className="col-subject">{getSubjectName(appointment)}</div>

												<div className="col-count">
													<span className={appointment?.completedAppointmentCount === 0 ? 'first-session' : ''}>
														{appointment?.completedAppointmentCount === 0 ? 'Bu ilk görüşme!' : appointment?.completedAppointmentCount + 1}
													</span>
												</div>

												<div className="col-actions">
													{activeTab === 'active' ? (
														<>
															{statusData.status === 'ACTIVE' &&
																(hasActiveMeeting && callState === CALL_STATE_TYPES.CONNECT ? (
																	<div className="btn-in-progress">
																		<i className="icons-danger-circle" />
																		Devam Ediyor
																	</div>
																) : (
																	<>
																		<button
																			className="btn-connect"
																			onClick={() => onClickhandleNavigateMeetingRoom(appointment)}>
																			<i className="icons-videocamera" />
																			Bağlan
																		</button>
																		{isActiveFromOutsideMeetLink && (
																			<div
																				className={clsx('connect-alternative-link', {
																					disabled: sendBackupRoomInvitationToStudentMutation?.isLoading || isSendedBackupLink
																				})}
																				onClick={() => onSendLinkToStudent(appointment)}>
																				<img src={CircleLink} />
																				{isSendedBackupLink ? 'Link gönderildi' : 'Öğrenciye Link Gönder'}
																			</div>
																		)}
																	</>
																))}

															{statusData.status === 'SCHEDULED' && (
																<div
																	className="btn-cancelled"
																	onClick={() => {
																		setSelectedAppointmentId(appointment.appointmentId);
																		setActionModalStep('confirm');
																		setSelectedDeleteReasonOption(undefined);
																		setCancellationReason('');
																		setIsActionModalOpen(true);
																	}}>
																	<i className="icons-close-circle" />
																	{renderActionModal(appointment.appointmentId)}
																	<span className="cancel-text">İptal Et</span>
																</div>
															)}

															{statusData.status === 'UPCOMING' && (
																<div className="results-container">
																	<div className="action-timer">
																		<i className="icons-clock-circle" />
																		<span>{statusData.formattedTimeLeft}</span>
																	</div>

																	{!statusData.isInOneHour && (
																		<i
																			className="icons-close-circle"
																			onClick={() => {
																				setSelectedAppointmentId(appointment.appointmentId);
																				setIsActionModalOpen(true);
																			}}
																		/>
																	)}
																	{renderActionModal(appointment.appointmentId)}
																</div>
															)}

															{statusData.status === 'COMPLETED' && (
																<>
																	<button
																		className="btn-complete"
																		onClick={() => handleCompleteReport(appointment)}>
																		Raporu Tamamla
																	</button>
																</>
															)}
														</>
													) : (
														<div className="cancel-reason">{getCancellationMessage(appointment?.appointmentUpdaterType)}</div>
													)}
												</div>
											</div>
										);
									})}
								</div>
							</div>

							{totalPages > 1 && (
								<Pagination
									currentPage={currentPage}
									totalPages={totalPages}
									onPageChange={setCurrentPage}
								/>
							)}
						</>
					)}
				</>
			)}

			<div className="slot-management">
				{dayjs(selectedDay.date).tz(TIMEZONE).isSame(dayjs(), 'day') && activeTab !== 'cancelled' && (
					<div className="slot-header">
						<div className="header-left">
							<h2>Bugün görüşme almayı aktifleştir</h2>
							<p>Öğrenciler açık saatlerinizin hepsine randevu alabilirler. Bugün slotlarınızın açık olduğu saatlerde bilgisayar başında olmanız gerekmektedir.</p>
						</div>
						<div className="toggle-section">
							<div className="toggle">
								<input
									type="checkbox"
									id="toggle"
									checked={isSlotSectionOpen}
									onChange={handleSlotToggle}
								/>
								<label htmlFor="toggle"></label>
							</div>
						</div>
					</div>
				)}

				{showSlotGrid && (
					<div className="slot-part">
						<div className="slot-header-container">
							<h3>{dayjs(selectedDay.date).tz(TIMEZONE).isSame(dayjs(), 'day') ? 'Bugünün Randevu Saatlerini Yönet' : 'Diğer Randevu Saatlerini Yönet'}</h3>
						</div>
						<div className="slot-grid">
							{availableSlots
								.filter(slot => slot.slotStatus !== 'FULL')
								.map((slot, index) => {
									const isActive = isSlotActive(slot);
									return (
										<div
											key={slot.id + index.toString()}
											className={`slot ${isActive ? 'active' : ''}`}
											onClick={e => handleSlotClick(e, slot)}>
											<i className={isActive ? 'icons-minus-circle' : 'icons-add-circle'} />
											{slot.time}
										</div>
									);
								})}
						</div>
						<div
							className={`save-changes-button ${!addedSlots.length && !removedSlots.length ? 'disabled' : ''}`}
							onClick={() => {
								if (addedSlots.length || removedSlots.length) {
									handleSaveChanges();
								}
							}}>
							Değişiklikleri Kaydet
						</div>
					</div>
				)}
			</div>

			<DialogModal
				isOpen={isModalOpen}
				onClose={handleCloseSlotWarningModal}
				variant="warning"
				title="Slot Uyarısı"
				primaryButtonText="Anladım"
				onPrimaryClick={handleCloseSlotWarningModal}
				className="appointment-info-modal">
				<p className="modal-text">
					Daha fazla slot kapatamazsınız. Aktif günlerdeki açık slot sayınız <strong>60 slotun</strong> altına düşemez. Slot kapatabilmek için öncelikle yeni slotlar açın.
				</p>
			</DialogModal>
		</div>
	);
};

export default DailyAppointments;
